import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule, ButtonModule],
  template: `
    <div class="callback-container">
      <div *ngIf="isLoading" class="loading-state">
        <p-progressSpinner 
          strokeWidth="4" 
          fill="transparent"
          animationDuration="1s"
          [style]="{ width: '50px', height: '50px' }"
          styleClass="spinner">
        </p-progressSpinner>
        <h3>Authenticating...</h3>
        <p>Please wait while we complete your login</p>
      </div>

      <div *ngIf="showTimeoutMessage" class="timeout-state">
        <i class="pi pi-exclamation-triangle timeout-icon"></i>
        <h3>Authentication is taking longer than usual</h3>
        <p>This might be due to a slow connection or temporary issue.</p>
        
        <div class="button-group">
          <p-button 
            (onClick)="retry()" 
            icon="pi pi-refresh" 
            label="Try Again"
            styleClass="retry-btn">
          </p-button>
          <p-button 
            (onClick)="backToLogin()" 
            icon="pi pi-arrow-left" 
            label="Back to Login"
            styleClass="back-btn"
            severity="secondary">
          </p-button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .callback-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 50vh;
      text-align: center;
      padding: 2rem;
    }

    .loading-state, .timeout-state {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .loading-state .spinner {
      margin-bottom: 2rem;
    }

    .loading-state h3, .timeout-state h3 {
      margin-bottom: 1rem;
      color: #333;
      font-weight: 500;
    }

    .loading-state p, .timeout-state p {
      color: #666;
      margin: 0 0 2rem 0;
    }

    .timeout-icon {
      font-size: 3rem;
      color: #f59e0b;
      margin-bottom: 1.5rem;
    }

    .button-group {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      justify-content: center;
    }

    .retry-btn, .back-btn {
      min-width: 140px;
    }
  `]
})
export class AuthCallbackComponent implements OnInit {
  isLoading = true;
  showTimeoutMessage = false;

  constructor(private authService: AuthService, private router: Router) {}
  private readonly MAX_AUTH_ATTEMPTS = 12;
  private readonly AUTH_POLL_INTERVAL_MS = 500;

  async ngOnInit() {
    await this.attemptAuthentication();
  }

  private async attemptAuthentication() {
    const user = await this.waitForAuth();
    
    if (user) {
      this.router.navigate(['/']);
    } else {
      this.isLoading = false;
      this.showTimeoutMessage = true;
    }
  }

  async retry() {
    this.showTimeoutMessage = false;
    this.isLoading = true;
    await this.attemptAuthentication();
  }

  backToLogin() {
    this.router.navigate(['/login']);
  }

  private async waitForAuth(): Promise<any> {
    for (let i = 0; i < this.MAX_AUTH_ATTEMPTS; i++) {
      const user = await this.authService.getUser();
      if (user) return user;
      await new Promise(resolve => setTimeout(resolve, this.AUTH_POLL_INTERVAL_MS));
    }
    return null;
  }
}